import { ChangeEvent, FC, useEffect, useState } from 'react';

import { SearchOutlined } from '@ant-design/icons';
import { Input } from 'antd';
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint';
import { useTheme } from 'context/ThemeContext';
import { setNavSearchValue } from 'redux/slices/businessListSlice';
import { useAppDispatch, useAppSelector } from 'redux/store';
import { useDebouncedCallback } from 'use-debounce';
import './NavSearch.css'; // Import the CSS file we'll create

interface Props {
  placeholder: string;
}

export const NavSearch: FC<Props> = ({ placeholder }) => {
  const dispatch = useAppDispatch();
  const { navSearchValue } = useAppSelector((state) => state.businessList);
  const [searchValue, setSearchValue] = useState(navSearchValue || '');
  const { isDarkMode } = useTheme();
  const { xs, sm, md } = useBreakpoint();

  const handleCustomerSearchChange = useDebouncedCallback((event: ChangeEvent<HTMLInputElement>) => {
    dispatch(setNavSearchValue(event.target.value));
  }, 300); // Increased debounce time for better performance

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.target.value);
    handleCustomerSearchChange(event);
  };

  useEffect(() => {
    setSearchValue(navSearchValue);
  }, [navSearchValue]);

  useEffect(() => {
    return () => {
      dispatch(setNavSearchValue(''));
      setSearchValue('');
    };
  }, []);

  // Calculate responsive width based on breakpoints
  const getInputWidth = () => {
    if (xs) return '100%';
    if (sm) return '250px';
    return '300px';
  };

  // Enhanced theme-aware styles with better color contrast
  const darkModeStyles = {
    backgroundColor: 'rgba(31, 30, 46, 0.8)',
    border: '1px solid rgb(69, 68, 76)',
    color: '#ffffff',
    width: getInputWidth(),
    boxShadow: 'none',
    height: 40
  };

  const lightModeStyles = {
    backgroundColor: 'red',
    border: '1px solid #d9d9d9',
    color: 'rgba(0, 0, 0, 0.85)',
    width: getInputWidth(),
    boxShadow: 'none',
    height: 40
  };

  // Create a style object for the input
  const inputStyle = {
    ...(isDarkMode ? darkModeStyles : lightModeStyles)
  };

  // Add a custom class to the document for global styling
  useEffect(() => {
    // Create a style element
    const styleEl = document.createElement('style');

    // Define comprehensive CSS for placeholder with red color
    const css = `
      /* Standard placeholder */
      .ant-input::placeholder,
      input::placeholder,
      .nav-search-input input::placeholder,
      .ant-input-affix-wrapper input::placeholder {
        color: red !important;
      }
      
      /* Firefox */
      .ant-input::-moz-placeholder,
      input::-moz-placeholder,
      .nav-search-input input::-moz-placeholder,
      .ant-input-affix-wrapper input::-moz-placeholder {
        color: red !important;
        opacity: 1;
      }
      
      /* Internet Explorer */
      .ant-input:-ms-input-placeholder,
      input:-ms-input-placeholder,
      .nav-search-input input:-ms-input-placeholder,
      .ant-input-affix-wrapper input:-ms-input-placeholder {
        color: red !important;
      }
      
      /* Edge */
      .ant-input::-ms-input-placeholder,
      input::-ms-input-placeholder,
      .nav-search-input input::-ms-input-placeholder,
      .ant-input-affix-wrapper input::-ms-input-placeholder {
        color: red !important;
      }
      
      /* Chrome/Safari */
      .ant-input::-webkit-input-placeholder,
      input::-webkit-input-placeholder,
      .nav-search-input input::-webkit-input-placeholder,
      .ant-input-affix-wrapper input::-webkit-input-placeholder {
        color: red !important;
      }
    `;

    styleEl.appendChild(document.createTextNode(css));
    document.head.appendChild(styleEl);

    // Clean up
    return () => {
      document.head.removeChild(styleEl);
    };
  }, [isDarkMode]);

  return (
    <Input
      prefix={<SearchOutlined style={{ color: isDarkMode ? 'rgba(255, 255, 255, 0.8)' : 'rgba(0, 0, 0, 0.65)', fontSize: 20 }} />}
      placeholder={placeholder}
      value={searchValue}
      onChange={handleChange}
      style={inputStyle}
      className={`${isDarkMode ? 'dark-mode-input' : 'light-mode-input'} nav-search-input`}
      // Add a key prop to force re-render when theme changes
      key={isDarkMode ? 'dark' : 'light'}
    />
  );
};
